'use client'

import {cx} from '@local/utils/cx'
import {SanityImage, SlideshowNative} from '@local/ui/components'
import {useRef} from 'react'
import {SanityImageType} from '@local/types'
import {useResizeObserver} from '@local/hooks'

export interface GalleryProps {
	slides: Array<SanityImageType>
	className?: string
	slideshowClassName?: string
	/** `parallaxAmplitude` is a value between 0 and 1 that will be converted into a percentage. */
	parallaxAmplitude?: number
}

export const Gallery = ({slides, className, slideshowClassName, parallaxAmplitude = 0.2}: GalleryProps) => {
	const slideshowRef = useRef(null)
	const scrollContainerWidth = useRef<number>(0)
	const childrenRefs = useRef<Array<HTMLDivElement>>([])

	const handleScroll = (e) => {
		const relativeScroll = e.target.scrollLeft / scrollContainerWidth.current

		childrenRefs.current.forEach((child, index) => {
			const _innerRatio = (relativeScroll * (slides.length - 1)) - index
			child.style.transform = `translate3d(${_innerRatio * parallaxAmplitude * 100}%,0,0)`
		})
	}

	useResizeObserver(slideshowRef, ({borderBoxSize}) => {
		// slideshowRef in this case is the screen width
		const slideshowWidth = borderBoxSize[0].inlineSize * slides.length
		const childWidth = borderBoxSize[0].inlineSize
		scrollContainerWidth.current = slideshowWidth - childWidth
	})

	if (!slides.length) return <></>

	return (
		<SlideshowNative
			containerRef={slideshowRef}
			containerClassName={cx('relative', className)}
			className={cx('w-full h-screen relative', slideshowClassName)}
			withDotNavigation
			dotNavigationClassName={cx(
				'absolute bottom-20 left-1/2 -translate-x-1/2 justify-center gap-14',
				'[&>button]:w-34 [&>button]:bg-white',
				'lg:hidden',
			)}
			withPrevNextNavigation
			prevNextNavigationClassName={cx(
				'lg-max:hidden',
				'absolute top-1/2 inset-0 margin-px-1 flex items-center justify-between z-10',
			)}
			prevNextButtonVariant='secondary'
			prevNextButtonClassName={cx(
				'lg-max:hidden',
				'rounded-md p-0 w-38 h-38 border-white',
				'disabled:bg-white disabled:text-black disabled:opacity-50',
			)}
			onScroll={handleScroll}>
			{slides.map((image, index) => (
				<div key={index} className='w-full overflow-clip'>
					<figure
						className='w-full h-full relative will-change-transform'
						ref={node => { childrenRefs.current[index] = node }}>
						<SanityImage
							image={image}
							width={375}
							height={700}
							mobileWidth={375}
							mobileOnly
							className='h-full w-full object-cover'
							fill />
						<SanityImage
							image={image}
							width={1440}
							height={900}
							desktopWidth={1440}
							desktopOnly
							className='h-full w-full object-cover'
							fill />
					</figure>
				</div>
			))}
		</SlideshowNative>
	)
}
